const mapsToOf = (q) => String(q?.maps_to || q?.mapsTo || "").trim();
const fieldUidOf = (q) =>
  String(q?.field_uid || q?.fieldUid || q?._id || "").trim();

const storeCustom = (bag, q, v) => {
  if (!bag || typeof bag !== "object") return;
  const key = fieldUidOf(q) || String(q?.question || "field");
  bag[key] = {
    label: q?.question || key,
    maps_to: mapsToOf(q) || "custom",
    value: v,
  };
};

const asScalar = (v) =>
  Array.isArray(v) ? v.join(", ") : v === undefined || v === null ? "" : String(v);

export const applyCustomerMapsTo = (customer, q, v, { isFileLike, toDocs }) => {
  const m = mapsToOf(q);
  if (!m || m === "section" || m.startsWith("pet.") || m.startsWith("consent."))
    return false;
  if (m === "custom") {
    storeCustom(customer.custom, q, v);
    return true;
  }
  switch (m) {
    case "customer.firstname":
      customer.firstname = asScalar(v);
      return true;
    case "customer.lastname":
      customer.lastname = asScalar(v);
      return true;
    case "customer.phone":
      customer.phone = asScalar(v);
      return true;
    case "customer.whatsapp_number":
      customer.whatsapp_number = asScalar(v);
      return true;
    case "customer.email":
      customer.email = asScalar(v);
      return true;
    case "customer.address.address1":
      customer.address.address1 = asScalar(v);
      return true;
    case "customer.address.address2":
      customer.address.address2 = asScalar(v);
      return true;
    case "customer.address.landmark":
      customer.address.landmark = asScalar(v);
      return true;
    case "customer.address.city":
      customer.address.city = asScalar(v);
      return true;
    case "customer.address.state":
      customer.address.state = asScalar(v);
      return true;
    case "customer.address.zip":
      customer.address.zip = asScalar(v);
      return true;
    case "customer.reference_source":
      customer.reference_source = asScalar(v);
      return true;
    case "customer.services_interested_in":
      customer.services_interested_in = Array.isArray(v) ? v : v ? [v] : [];
      return true;
    case "customer.notes":
      customer.notes = asScalar(v);
      return true;
    case "customer.signature":
      customer.signature = typeof v === "string" ? v : null;
      return true;
    case "customer.id_proof":
    case "customer.alternate_phone":
    case "customer.address.maps_url":
      if (isFileLike(v)) customer.documents.push(...toDocs(v, q, "document"));
      else storeCustom(customer.custom, q, v);
      return true;
    default:
      if (m.startsWith("customer.")) {
        if (isFileLike(v)) customer.documents.push(...toDocs(v, q, "document"));
        else storeCustom(customer.custom, q, v);
        return true;
      }
      return false;
  }
};

export const applyPetMapsTo = (pet, q, v, { isFileLike, toDocs, asList, labelFor, idx }) => {
  const m = mapsToOf(q);
  if (!m || m === "section" || m.startsWith("customer.") || m.startsWith("consent."))
    return false;
  if (m === "custom") {
    storeCustom(pet.custom, q, v);
    return true;
  }
  pet._vac = pet._vac || {};
  pet._med = pet._med || {};
  pet._meal = pet._meal || {};

  switch (m) {
    case "pet.name":
      pet.name = asScalar(v);
      return true;
    case "pet.pet_type":
      pet.pet_type = labelFor("pet_type", v, idx);
      return true;
    case "pet.breed":
      pet.breed = labelFor("breed", v, idx);
      return true;
    case "pet.size":
      pet.size = labelFor("size", v, idx);
      return true;
    case "pet.gender":
      pet.gender = asScalar(v);
      return true;
    case "pet.birthday":
      pet.birthday = v;
      return true;
    case "pet.age":
      pet.age = asScalar(v);
      return true;
    case "pet.weight_kg": {
      const n = Number(v);
      pet.weight_kg = Number.isNaN(n) ? v : n;
      return true;
    }
    case "pet.color":
    case "pet.hair_color":
      pet.color = Array.isArray(v) ? v : [v];
      return true;
    case "pet.hair_type":
      pet.hair_type = asScalar(v);
      return true;
    case "pet.hair_length":
      pet.hair_length = asScalar(v);
      return true;
    case "pet.neutered_status":
      pet.neutered_status = asScalar(v);
      return true;
    case "pet.vaccination_status":
      pet.vaccination_status = asScalar(v);
      return true;
    case "pet.vaccinations":
      pet.vaccinations = Array.isArray(v) ? v : [v];
      return true;
    case "pet.medical_conditions":
      pet.medical_conditions = Array.isArray(v) ? v : [v];
      return true;
    case "pet.custom_medical_condition":
      pet.custom_medical = v;
      return true;
    case "pet.allergies":
      pet.allergies = Array.isArray(v) ? v : [v];
      return true;
    case "pet.meal_type":
      pet.meal_type = Array.isArray(v) ? v : v ? [v] : [];
      return true;
    case "pet.meals_per_day":
      pet.meals_per_day = asScalar(v);
      return true;
    case "pet.walks_per_day":
      pet.walks_per_day = asScalar(v);
      return true;
    case "pet.behavior_notes":
    case "pet.behaviour":
    case "pet.behavior":
      pet.behavior_notes = asScalar(v);
      return true;
    case "pet.special_care_instructions":
    case "pet.special_instructions":
    case "pet.special_medical_instructions":
      pet.special_care_instructions = [pet.special_care_instructions, asScalar(v)]
        .filter(Boolean)
        .join("\n");
      return true;
    case "pet.photo":
      if (isFileLike(v)) pet.photo = asList(v)[0];
      else pet.image = v;
      return true;
    case "pet.signature":
      pet.signature = typeof v === "string" ? v : null;
      return true;
    case "pet.document_vaccination":
      pet.vaccination_record = [...asList(pet.vaccination_record), ...asList(v)];
      return true;
    case "pet.document_medical":
      pet.medical_record = [...asList(pet.medical_record), ...asList(v)];
      return true;
    case "pet.document_prescription":
      pet.prescription = [...asList(pet.prescription), ...asList(v)];
      return true;
    case "pet.document_lab":
      pet.lab_report = [...asList(pet.lab_report), ...asList(v)];
      return true;
    case "pet.document_insurance":
      pet.insurance_document = [...asList(pet.insurance_document), ...asList(v)];
      return true;
    case "pet.vaccine_document":
      pet._vac.documents = asList(v);
      if (isFileLike(v))
        pet.vaccination_record = [...asList(pet.vaccination_record), ...asList(v)];
      return true;
    case "pet.vaccine":
      pet._vac.vaccine = asScalar(v);
      return true;
    case "pet.vaccine_given_on":
      pet._vac.date_given = v;
      return true;
    case "pet.vaccine_next_due":
      pet._vac.next_due = v;
      return true;
    case "pet.vaccine_administered_by":
      pet._vac.administered_by = asScalar(v);
      return true;
    case "pet.vaccine_batch":
      pet._vac.batch_number = asScalar(v);
      return true;
    case "pet.vaccine_notes":
      pet._vac.notes = asScalar(v);
      return true;
    case "pet.medication_name":
      pet._med.name = asScalar(v);
      return true;
    case "pet.medication_dosage":
    case "pet.medication_dosage_unit":
      pet._med.dosage = [pet._med.dosage, asScalar(v)].filter(Boolean).join(" ");
      return true;
    case "pet.medication_time":
      pet._med.time = asScalar(v);
      return true;
    case "pet.medication_instructions":
      pet._med.notes = asScalar(v);
      return true;
    case "pet.meal_name":
      pet._meal.name = asScalar(v);
      return true;
    case "pet.meal_slot":
      pet._meal.slot = asScalar(v);
      return true;
    case "pet.meal_time":
      pet._meal.time = asScalar(v);
      return true;
    case "pet.meal_quantity":
    case "pet.meal_quantity_unit":
      pet._meal.quantity = [pet._meal.quantity, asScalar(v)].filter(Boolean).join(" ");
      return true;
    case "pet.feeding_instructions":
    case "pet.custom_meal_notes":
    case "pet.hydration":
      pet._meal.notes = [pet._meal.notes, asScalar(v)].filter(Boolean).join("\n");
      return true;
    default: {
      const vaccMatch = /^pet\.(.+)_vaccination_(date|image)$/.exec(m);
      if (vaccMatch) {
        const [, slug, kind] = vaccMatch;
        const rawName = String(q?.question || slug)
          .replace(/\s*vaccination\s*(date|image)\s*$/i, "")
          .trim();
        pet._vacBySlug = pet._vacBySlug || {};
        pet._vacBySlug[slug] = pet._vacBySlug[slug] || {
          vaccine: rawName,
          date_given: null,
          next_due: null,
          notes: "",
          documents: [],
        };
        if (kind === "date") {
          pet._vacBySlug[slug].date_given = v;
        } else if (isFileLike(v)) {
          pet._vacBySlug[slug].documents.push(...toDocs(v, q, "vaccination"));
        }
        return true;
      }
      if (m.startsWith("pet.")) {
        if (isFileLike(v)) pet.documents.push(...toDocs(v, q, "document"));
        else storeCustom(pet.custom, q, v);
        return true;
      }
      return false;
    }
  }
};

export const finalizeMappedPet = (pet) => {
  if (pet._vacBySlug) {
    pet.vaccination_records = pet.vaccination_records || [];
    for (const rec of Object.values(pet._vacBySlug)) {
      if (rec.date_given || (rec.documents && rec.documents.length > 0)) {
        pet.vaccination_records.push(rec);
      }
    }
    delete pet._vacBySlug;
  }
  if (pet._vac && (pet._vac.vaccine || pet._vac.date_given)) {
    pet.vaccination_records = [
      ...(pet.vaccination_records || []),
      {
        vaccine: pet._vac.vaccine || "Vaccination",
        date_given: pet._vac.date_given || null,
        next_due: pet._vac.next_due || null,
        notes: pet._vac.notes || "",
        documents: pet._vac.documents || [],
      },
    ];
  }
  if (
    pet._med &&
    (pet._med.name || pet._med.dosage || pet._med.time) &&
    !(pet.ongoing_medications || []).length
  ) {
    pet.ongoing_medications = [
      {
        name: pet._med.name || "",
        dosage: pet._med.dosage || "",
        time: pet._med.time || "",
        notes: pet._med.notes || "",
      },
    ];
  }
  if (
    pet._meal &&
    (pet._meal.name || pet._meal.time || pet._meal.quantity) &&
    !(pet.meals || []).length
  ) {
    pet.meals = [
      {
        name: pet._meal.name || pet._meal.slot || "",
        time: pet._meal.time || "",
        quantity: pet._meal.quantity || "",
        notes: pet._meal.notes || "",
        added_date: new Date().toISOString(),
      },
    ];
  }
  delete pet._vac;
  delete pet._med;
  delete pet._meal;
};

const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    const s = String(v);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const pickCustom = (bag, q) => {
  if (!bag || typeof bag !== "object") return undefined;
  const uid = fieldUidOf(q);
  if (bag[uid] && bag[uid].value !== undefined) return bag[uid].value;
  const label = String(q?.question || "").toLowerCase().trim();
  const hit = Object.values(bag).find(
    (x) => x && String(x.label || "").toLowerCase().trim() === label,
  );
  return hit?.value;
};

const customerVal = (c, q) => {
  const m = mapsToOf(q);
  const t = String(q?.question || "").toLowerCase();
  if (m === "customer.firstname" || /\bfirst\s*name\b/.test(t)) return c.firstname;
  if (m === "customer.lastname" || /\blast\s*name\b/.test(t)) return c.lastname;
  if (m === "customer.phone") return c.phone;
  if (m === "customer.whatsapp_number" || /whats\s*app/.test(t)) return c.whatsapp_number;
  if (m === "customer.email" || /\bemail\b/.test(t)) return c.email;
  if (m === "customer.address.address1" || /address\s*(line\s*)?1|home\s*address/.test(t))
    return c.address?.address1;
  if (m === "customer.address.address2" || /address\s*(line\s*)?2/.test(t))
    return c.address?.address2;
  if (m === "customer.address.landmark" || /landmark/.test(t)) return c.address?.landmark;
  if (m === "customer.address.city" || /\bcity\b/.test(t)) return c.address?.city;
  if (m === "customer.address.state" || /\bstate\b/.test(t)) return c.address?.state;
  if (m === "customer.address.zip" || /pin\s*code|pincode|\bzip\b/.test(t)) return c.address?.zip;
  if (m === "customer.reference_source" || /customer\s*ref|referred\s*by|reference/.test(t))
    return c.reference_source;
  if (m === "customer.services_interested_in" || /services?\s*interest/.test(t))
    return c.services_interested_in;
  if (m === "customer.notes" || /\bnotes?\b|comments?/.test(t)) return c.notes;
  if (m === "customer.signature") return c.signature;
  return pickCustom(c.custom, q);
};

const petVal = (p, q) => {
  if (!p) return undefined;
  const m = mapsToOf(q);
  const t = String(q?.question || "").toLowerCase();
  if (m === "pet.name" || /pet\s*name/.test(t)) return p.name;
  if (m === "pet.pet_type" || /pet\s*type/.test(t)) return p.pet_type;
  if (m === "pet.breed" || /\bbreed\b/.test(t)) return p.breed;
  if (m === "pet.size" || /\bsize\b/.test(t)) return p.size;
  if (m === "pet.gender" || /\bgender\b|\bsex\b/.test(t)) return p.gender;
  if (m === "pet.birthday" || /birthday|dob|date\s*of\s*birth/.test(t))
    return toDateInput(p.birthday);
  if (m === "pet.age" || (/\bage\b/.test(t) && !/dob|birth/.test(t))) return p.age;
  if (m === "pet.weight_kg" || (/\bweight\b/.test(t) && !/unit/.test(t))) return p.weight_kg;
  if (m === "pet.color" || /\bcolou?r\b/.test(t)) return p.color;
  if (m === "pet.hair_type" || /hair\s*type/.test(t)) return p.hair_type;
  if (m === "pet.hair_length" || /hair\s*length/.test(t)) return p.hair_length;
  if (m === "pet.neutered_status" || /neutered|spayed/.test(t)) return p.neutered_status;
  if (m === "pet.vaccination_status" || /vaccination\s*status/.test(t)) return p.vaccination_status;
  if (m === "pet.vaccinations") return p.vaccinations;
  const vm = /^pet\.(.+)_vaccination_(date|image)$/.exec(m);
  if (vm && vm[2] === "date") {
    const slug = vm[1].toLowerCase();
    const hit = (p.vaccination_records || [])
      .filter(
        (r) =>
          String(r.vaccine || "").toLowerCase().includes(slug) ||
          slug.includes(String(r.vaccine || "").toLowerCase()) ||
          (slug.includes("dhpp") && String(r.vaccine || "").toLowerCase().includes("dhpp"))
      )
      .sort((a, b) => new Date(b.date_given || 0) - new Date(a.date_given || 0))[0];
    if (hit?.date_given) return toDateInput(hit.date_given);
  }
  if (m === "pet.medical_conditions" || /medical\s*condition/.test(t)) return p.medical_conditions;
  if (m === "pet.allergies" || (/\ballerg/.test(t) && !/details|severity/.test(t))) return p.allergies;
  if (m === "pet.meal_type" || /diet\s*type|meal\s*type/.test(t)) return p.meal_type;
  if (m === "pet.meals_per_day" || /meals?\s*per\s*day/.test(t)) return p.meals_per_day;
  if (m === "pet.walks_per_day" || /walks?\s*per\s*day/.test(t)) return p.walks_per_day;
  if (m === "pet.behavior_notes" || /behaviou?r/.test(t)) return p.behavior_notes;
  if (m === "pet.special_care_instructions" || /special\s*care/.test(t))
    return p.special_care_instructions;
  if (m === "pet.photo" || /pet\s*profile|pet.*photo/.test(t))
    return p.photo || (p.image ? { name: "Pet photo", url: p.image, key: p.image_key } : undefined);
  if (m === "pet.signature") return p.signature;
  return pickCustom(p.custom, q);
};

export const hydrateAnswersFromSubmission = ({
  customerQuestions = [],
  petQuestions = [],
  consentQ = [],
  previous,
}) => {
  const answers = {};
  if (!previous?.found && !previous?.customer) {
    return { answers, petCount: 1, consent: { agreed: false, signature: "" } };
  }
  const c = previous.customer || {};
  const pets = Array.isArray(previous.pets) && previous.pets.length ? previous.pets : [{}];
  const cons = previous.consent || {};

  customerQuestions.forEach((q) => {
    if (q.answerType === "section_heading") return;
    const v = customerVal(c, q);
    if (v === undefined || v === null || v === "") return;
    answers[q._id] = v;
  });

  pets.forEach((p, i) => {
    petQuestions.forEach((q) => {
      if (q.answerType === "section_heading") return;
      const v = petVal(p, q);
      if (v === undefined || v === null || v === "") return;
      answers[`${q._id}__${i}`] = v;
    });
    if (Array.isArray(p.meals) && p.meals.length) {
      answers[`diet__${i}__items`] = p.meals.map((m) => ({
        what: m.name || "",
        when: m.time ? [m.time] : m.when || [],
        how: m.quantity || "",
        instructions: m.notes || "",
        added_date: m.added_date || null,
      }));
    }
    if (Array.isArray(p.ongoing_medications) && p.ongoing_medications.length) {
      answers[`meds__${i}__items`] = p.ongoing_medications.map((m) => ({
        name: m.name || "",
        dosage: m.dosage || "",
        when: m.time ? String(m.time).split(", ") : [],
        instructions: m.notes || m.instructions || "",
      }));
    }
  });

  (cons.answers || []).forEach((a) => {
    const q = consentQ.find(
      (x) => String(x.question || "").trim() === String(a.question || "").trim(),
    );
    if (q && a.answer) answers[q._id] = a.answer;
  });

  return {
    answers,
    petCount: Math.max(1, pets.length),
    consent: {
      agreed: cons.accepted === true,
      signature: cons.signature || c.signature || "",
    },
  };
};
